import { resolveSymbolicDate } from "@Simplicity-Education/core";
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useEffect, useRef, useState } from "react";

/**
 * Default set of named tokens we surface in the dropdown. Anything else is a
 * "Custom" entry. Callers can override via the `tokens` prop to slim the list
 * per-context (e.g. project-level vs. per-cadence-override).
 */
const DEFAULT_NAMED_TOKENS = ["TODAY", "P-1D", "P-7D", "P-30D", "SCHOOLYEARSTART"] as const;
const CUSTOM_SENTINEL = "__custom__";
const NONE_SENTINEL = "__none__";
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

interface SinceDateInputProps {
  id?: string;
  /** Effective value at this layer (after inheritance). May be "" for none. */
  value: string;
  /** Active override at THIS layer, distinct from the inherited effective value. */
  override?: string | undefined;
  /** Inheritance source — when set and `override` undefined, displayed dimmed. */
  inheritedFrom?: "project" | "table" | null;
  /** Project `schoolYearStart` used by SCHOOLYEARSTART resolution (MM-DD). */
  schoolYearStart?: string;
  /** Frozen "now" for testability. Defaults to `new Date()`. */
  now?: Date;
  /**
   * Named tokens shown in the dropdown (in order). Defaults to the full set.
   * `Custom…` and `(none)` entries are always present regardless of this prop.
   */
  tokens?: readonly string[];
  onChange: (next: string) => void;
  disabled?: boolean;
}

interface DerivedState {
  isOverride: boolean;
  dimmed: boolean;
  effective: string;
  isNamed: boolean;
  isCustom: boolean;
  selectValue: string;
  resolved: string;
  showResolved: boolean;
}

function derive(props: SinceDateInputProps, now: Date, tokens: readonly string[]): DerivedState {
  const { value, override, inheritedFrom, schoolYearStart } = props;
  const isOverride = override !== undefined;
  const dimmed = !isOverride && inheritedFrom != null;
  const effective = isOverride ? (override ?? "") : value;
  const isNamed = tokens.includes(effective);
  const isCustom = !isNamed && effective !== "";
  const selectValue = isNamed ? effective : isCustom ? CUSTOM_SENTINEL : NONE_SENTINEL;
  const resolved = effective ? resolveSymbolicDate(effective, { now, schoolYearStart }) : "";
  const showResolved = effective !== "" && resolved !== effective;
  return {
    isOverride,
    dimmed,
    effective,
    isNamed,
    isCustom,
    selectValue,
    resolved,
    showResolved,
  };
}

function TokenDropdown(props: {
  id?: string;
  selectValue: string;
  dimmed: boolean;
  disabled?: boolean;
  tokens: readonly string[];
  onSelect: (next: string) => void;
}) {
  return (
    <Select value={props.selectValue} onValueChange={props.onSelect} disabled={props.disabled}>
      <SelectTrigger
        id={props.id}
        className={
          "h-7 w-44 font-mono text-xs " + (props.dimmed ? "text-muted-foreground italic" : "")
        }
      >
        <SelectValue placeholder="Select token…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE_SENTINEL}>(none)</SelectItem>
        {props.tokens.map((t) => (
          <SelectItem key={t} value={t}>
            {t}
          </SelectItem>
        ))}
        <SelectItem value={CUSTOM_SENTINEL}>Custom…</SelectItem>
      </SelectContent>
    </Select>
  );
}

/**
 * Symbolic-date dropdown plus custom input for `vars.filters.sinceDate` and the Slice C override.
 * Offers named tokens, `Custom…` and `(none)`, shows the resolved date inline, and renders dimmed
 * with `(from <layer>)` when inheriting. SIM-1638: "just picked Custom…" is tracked in local state,
 * because the derivation set `effective` to `""` and snapped to `(none)`.
 */
function CustomDateInput(props: {
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
}) {
  // SIM-1638: prefer `<input type="date">` for empty + ISO-shaped values;
  // fall back to `<input type="text">` for legacy non-ISO custom tokens
  // (e.g. `P-90D`) so existing configs stay editable.
  const useDateInput = props.value === "" || ISO_DATE_RE.test(props.value);
  return (
    <Input
      type={useDateInput ? "date" : "text"}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={useDateInput ? "YYYY-MM-DD" : "YYYY-MM-DD or token"}
      disabled={props.disabled}
      className="h-7 w-36 font-mono text-xs"
      aria-label="Custom sinceDate value"
    />
  );
}

function ResolvedDate(props: { resolved: string }) {
  return (
    <span className="font-mono text-xs text-muted-foreground">
      <span aria-hidden="true">→ </span>
      <span>{props.resolved}</span>
    </span>
  );
}

function makeHandleSelect(
  setPickedCustom: (v: boolean) => void,
  onChange: (v: string) => void,
): (next: string) => void {
  return (next) => {
    if (next === CUSTOM_SENTINEL) {
      setPickedCustom(true);
      onChange("");
      return;
    }
    if (next === NONE_SENTINEL) {
      setPickedCustom(false);
      onChange("");
      return;
    }
    setPickedCustom(false);
    onChange(next);
  };
}

export function SinceDateInput(props: SinceDateInputProps) {
  const { id, inheritedFrom, onChange, disabled } = props;
  const now = props.now ?? new Date();
  const tokens = props.tokens ?? DEFAULT_NAMED_TOKENS;
  const s = derive(props, now, tokens);

  // SIM-1638: local UI state — true while the user has just chosen Custom…
  // but hasn't committed a value yet. Reset whenever the underlying value
  // becomes a named token (user changed their mind).
  const [pickedCustom, setPickedCustom] = useState(false);
  const prevIsNamed = useRef(s.isNamed);
  useEffect(() => {
    if (s.isNamed && !prevIsNamed.current) {
      setPickedCustom(false);
    }
    prevIsNamed.current = s.isNamed;
  }, [s.isNamed]);

  const showCustomInput = s.isCustom || (s.isOverride && pickedCustom);
  // When the dropdown value would otherwise be `(none)` but the user just
  // picked Custom…, surface that selection in the trigger.
  const trigger = pickedCustom && !s.isNamed && !s.isCustom ? CUSTOM_SENTINEL : s.selectValue;
  const handleSelect = makeHandleSelect(setPickedCustom, onChange);

  return (
    <div data-testid="since-date-input">
      <div className="flex items-center gap-2">
        <TokenDropdown
          id={id}
          selectValue={trigger}
          dimmed={s.dimmed}
          disabled={disabled}
          tokens={tokens}
          onSelect={handleSelect}
        />
        {showCustomInput && (
          <CustomDateInput value={s.effective} disabled={disabled} onChange={onChange} />
        )}
        {s.showResolved && <ResolvedDate resolved={s.resolved} />}
      </div>
      {s.dimmed && inheritedFrom && (
        <p className="mt-1 text-xs text-muted-foreground italic">(from {inheritedFrom})</p>
      )}
      <p className="mt-1 text-xs text-muted-foreground">Resolves in America/New_York.</p>
    </div>
  );
}
